/**
 * Utilitaires pour le calcul des configurations visuelles du mannequin
 * à partir des mensurations calculées par /api/profile/compute.
 */

// Valeurs de base des bandes par genre et catégorie morphologique.
// Ces valeurs doivent être cohérentes avec les proportions visuelles
// des images mannequin (slim/average/large).
const BAND_BASE_RX = {
  homme: {
    slim:    { chest: 23, waist: 18, hips: 21.5 },
    average: { chest: 22, waist: 21, hips: 23 },
    large:   { chest: 24, waist: 24, hips: 25 },
  },
  femme: {
    slim:    { chest: 21, waist: 21, hips: 24 },
    average: { chest: 23, waist: 22, hips: 25 },
    large:   { chest: 25, waist: 26, hips: 28 },
  },
};

const BAND_SCALES = {
  chest: 0.4,
  waist: 0.6,
  hips:  0.6,
};
const BAND_RX_MIN = 8;
const BAND_RX_MAX = 45;

/**
 * Calcule la configuration des bandes (rx) pour FitMannequin.
 * La taille de base est toujours baseRx (neutre), seuls les deltas des sliders
 * font varier la largeur — évite que des mesures éloignées de la référence
 * produisent des bandes trop petites ou trop grandes à position neutre.
 *
 * @param {number} deltaChest               - valeur du slider poitrine (-2 à +2)
 * @param {number} deltaWaist               - valeur du slider taille (-2 à +2)
 * @param {number} deltaHips                - valeur du slider hanches (-2 à +2)
 * @param {string} gender                   - 'homme' ou 'femme'
 * @param {'slim'|'average'|'large'} category - catégorie morphologique du mannequin
 * @returns {Object} { chest: {rx}, waist: {rx}, hips: {rx} }
 */
export const computeBandConfig = (deltaChest, deltaWaist, deltaHips, gender, category = 'average') => {
  const g   = gender === 'homme' ? 'homme' : 'femme';
  const cat = ['slim', 'average', 'large'].includes(category) ? category : 'average';
  const base = BAND_BASE_RX[g][cat];

  const apply = (baseRx, delta, cmPerCran, scale) => {
    const rx = baseRx + delta * cmPerCran * scale;
    return Math.max(BAND_RX_MIN, Math.min(BAND_RX_MAX, rx));
  };

  return {
    chest: { rx: apply(base.chest, deltaChest, DELTA_CM_PER_CRAN.chest, BAND_SCALES.chest) },
    waist: { rx: apply(base.waist, deltaWaist, DELTA_CM_PER_CRAN.waist, BAND_SCALES.waist) },
    hips:  { rx: apply(base.hips,  deltaHips,  DELTA_CM_PER_CRAN.hips,  BAND_SCALES.hips)  },
  };
};

/**
 * Détermine la catégorie morphologique pour la sélection du mannequin.
 * Utilise le WHR (waist-hip ratio) depuis les mensurations calculées.
 * Fallback sur le ratio poids/taille si les mensurations ne sont pas disponibles.
 *
 * @param {Object|null} measurements - retour de /api/profile/compute
 * @param {number} height - taille en cm
 * @param {number} weight - poids en kg
 * @returns {'slim'|'average'|'large'}
 */
export const getMorphologyCategory = (measurements, height, weight) => {
  if (measurements) {
    const { waist_min, waist_max, hips_min, hips_max } = measurements;
    if (waist_min != null && waist_max != null && hips_min != null && hips_max != null) {
      const waistCenter = (waist_min + waist_max) / 2;
      const hipsCenter  = (hips_min  + hips_max)  / 2;
      if (hipsCenter > 0) {
        const whr = waistCenter / hipsCenter;
        if (whr < 0.80) return 'slim';
        if (whr < 0.93) return 'average';
        return 'large';
      }
    }
  }
  // Fallback : ratio poids/taille
  if (!weight || !height) return 'average';
  const ratio = weight / height;
  if (ratio < 0.38) return 'slim';
  if (ratio < 0.50) return 'average';
  return 'large';
};

/**
 * Calcule l'URL de l'image mannequin selon le genre et la catégorie morphologique.
 *
 * @param {string} gender - 'homme' ou 'femme'
 * @param {'slim'|'average'|'large'} category
 * @returns {string} URL relative vers public/mannequins/
 */
export const getMannequinSrc = (gender, category) => {
  const gCode = gender === 'homme' ? 'H' : 'F';
  return `${process.env.PUBLIC_URL}/mannequins/mannequin_${gCode}_${category}.jpg`;
};

export const DELTA_CM_PER_CRAN = { chest: 2.0, waist: 1.0, hips: 1.5 };

/**
 * Applique localement les différences de deltas sur les mensurations de base.
 * Permet de recalculer les bandes en temps réel sans appel API.
 *
 * @param {Object} baseMeasurements - mensurations calculées à l'état initDeltas
 * @param {{chest, waist, hips}} initDeltas - deltas au moment du calcul de baseMeasurements
 * @param {{chest, waist, hips}} currentDeltas - deltas actuels des sliders
 * @returns {Object} mensurations ajustées
 */
export const applyLocalDeltas = (baseMeasurements, initDeltas, currentDeltas) => {
  const chestAdj = (currentDeltas.chest - initDeltas.chest) * DELTA_CM_PER_CRAN.chest;
  const waistAdj = (currentDeltas.waist - initDeltas.waist) * DELTA_CM_PER_CRAN.waist;
  const hipsAdj  = (currentDeltas.hips  - initDeltas.hips)  * DELTA_CM_PER_CRAN.hips;

  return {
    ...baseMeasurements,
    chest_min: baseMeasurements.chest_min + chestAdj,
    chest_max: baseMeasurements.chest_max + chestAdj,
    waist_min: baseMeasurements.waist_min + waistAdj,
    waist_max: baseMeasurements.waist_max + waistAdj,
    hips_min:  baseMeasurements.hips_min  + hipsAdj,
    hips_max:  baseMeasurements.hips_max  + hipsAdj,
  };
};
